/**
 * Etapas do fluxo: Dados Básicos → Endereço → Plano/Pagamento → Execução de Pagamento.
 */
export const STEPS = {
    DADOS_BASICOS: 1,
    ENDERECO: 2,
    PLANO: 3,
    PAGAMENTO_EXEC: 4,
} as const;

export const STEP_NAMES: Record<number, string> = {
    1: 'dados_basicos',
    2: 'dados_endereco',
    3: 'plano',
    4: 'pagamento_exec',
};

export const STEP_SUBTITLES: Record<number, string> = {
    1: 'Informe seus dados pessoais',
    2: 'Informe seus dados de endereço',
    3: 'Selecione a forma de pagamento',
    4: 'Realize o pagamento',
};

export const STEP_TOTAL = 4;

export type SuccessVariant = 'payment_confirmed' | 'boleto_generated' | 'pix_pending';

export const PAYMENT_METHOD_MAP: Record<string, string> = {
    Pix: 'pix',
    'Cartão de Crédito': 'cartao',
    Cartão: 'cartao',
    Boleto: 'boleto',
};

export const CUSTOMER_TYPE_MAP: Record<string, string> = {
    'Público Geral': 'publico',
    Egresso: 'egresso',
};

/**
 * Injeta o snippet do Google Tag Manager no <head> do documento hospedeiro.
 * Idempotente: se já foi instalado para o mesmo container (ou por outro snippet
 * que já criou window.dataLayer com gtm.start), apenas garante o array dataLayer.
 *
 * O snippet GTM precisa rodar no document.head do host — não dentro do shadowRoot
 * do widget, senão GTM não consegue interceptar pushes para o dataLayer global.
 */
export function installGtm(containerId: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const id = String(containerId || '').trim().toUpperCase();
    if (!/^GTM-[A-Z0-9]+$/.test(id)) return;

    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>>; __decolaGtmInstalled?: Record<string, true> };
    w.dataLayer = w.dataLayer || [];
    w.__decolaGtmInstalled = w.__decolaGtmInstalled || {};
    if (w.__decolaGtmInstalled[id]) return;
    w.__decolaGtmInstalled[id] = true;

    const existing = document.querySelector(`script[src*="googletagmanager.com/gtm.js"][src*="${id}"]`);
    if (existing) return;

    w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
}

/**
 * Push em window.dataLayer com forma { event, ...payload }. Cria o array se ainda
 * não existir (caso o GTM não tenha sido carregado), assim os pushes não são perdidos
 * — quando o GTM carregar depois, ele consome o array existente.
 */
export function pushDataLayer(event: string, payload: Record<string, unknown> = {}): void {
    if (typeof window === 'undefined') return;
    const evt = String(event || '').trim();
    if (evt === '') return;
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: evt, ...payload });
}

export function escapeHtmlAttr(s: string): string {
    return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Aviso de confirmação de matrícula no checkout (parcelado vs à vista).
 */
export function enrollmentConfirmationNoteHtml(installments: number): string {
    const parcelado = installments > 1;
    const text = parcelado
        ? '* Sua matrícula será confirmada após pagamento da primeira parcela.'
        : '* Sua matrícula será confirmada após o pagamento.';
    return `<p class="payment-enrollment-confirmation-note" role="note">${escapeHtmlAttr(text)}</p>`;
}

export function formatIsoDateTimeForDisplay(iso: string): string {
    const t = typeof iso === 'string' ? iso.trim() : '';
    if (!t) return '';
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return t;
    try {
        return d.toLocaleString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return t;
    }
}

export function parsePrice(value: number | string): number {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    const s = String(value || '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
}

export function formatPrice(price: number | string): string {
    const n = parsePrice(price);
    return (
        'R$ ' +
        n.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
}

export function getPlanNetPrice(plan: { price?: number | string; discount?: number | string } | null): number {
    if (!plan) return 0;
    const valor = parsePrice(plan.price ?? 0);
    const desconto = parsePrice(plan.discount ?? 0);
    return Math.max(0, valor - desconto);
}

/** Desconto da bolsa (cupom): percentual (P) ou valor fixo (V) sobre o valor líquido do plano. */
export function computeScholarshipDiscount(
    baseNet: number,
    discountType: 'P' | 'V' | string,
    amount: number,
): number {
    if (baseNet <= 0 || amount < 0) return 0;
    const t = String(discountType).toUpperCase();
    if (t === 'P') {
        const raw = (baseNet * amount) / 100;
        return Math.min(baseNet, Math.round(raw * 100) / 100);
    }
    return Math.min(baseNet, Math.round(amount * 100) / 100);
}

export function calcParcelamento(total: number, parcelas: number): { valores: number[]; valorExibicao: number } {
    if (parcelas <= 1) return { valores: [total], valorExibicao: total };
    const base = Math.floor((total * 100) / parcelas) / 100;
    const valores = Array(parcelas - 1).fill(base);
    const ultima = Math.round((total - base * (parcelas - 1)) * 100) / 100;
    valores.push(ultima);
    return { valores, valorExibicao: valores[0] };
}

export type PaymentPlanRow = {
    id?: string;
    name?: string;
    deleted?: boolean;
    customerType?: string;
    paymentMethod?: string;
    price?: number | string;
    discount?: number | string;
    installments?: number | string;
    chargeAllInstallments?: boolean;
    description?: string;
};

/** Normaliza o método vindo da API (pix, boleto, credit_card, etc.) para as chaves do widget. */
export function normalizeWidgetPaymentMethod(raw: unknown): 'pix' | 'cartao' | 'boleto' | null {
    const s = String(raw ?? '')
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, '_');
    if (s === 'credit_card' || s === 'creditcard' || s === 'cartao' || s === 'cartão') {
        return 'cartao';
    }
    if (s === 'pix') {
        return 'pix';
    }
    if (s === 'boleto') {
        return 'boleto';
    }

    return null;
}

export type ApiCombinationLike = {
    payment_plan_id: number;
    admission_type_id: number;
    payment_method: string;
    installments: number;
    charge_all_installments?: boolean;
    amount: string;
    discount: string;
    name?: string | null;
    description?: string | null;
};

/**
 * Agrupa combinações oferta × tipo de ingresso × meio (vários planos com o mesmo meio viram lista).
 * Ignora admission_type_id inválido e métodos não reconhecidos.
 */
export function buildPlansFromCombinations(
    combinations: ApiCombinationLike[],
): Record<string, Record<string, PaymentPlanRow[]>> {
    const out: Record<string, Record<string, PaymentPlanRow[]>> = {};
    for (const c of combinations) {
        const atId = Number(c.admission_type_id);
        if (!Number.isFinite(atId) || atId <= 0) {
            continue;
        }
        const uiPm = normalizeWidgetPaymentMethod(c.payment_method);
        if (uiPm === null) {
            continue;
        }
        const atKey = `at_${atId}`;
        if (!out[atKey]) {
            out[atKey] = {};
        }
        if (!out[atKey][uiPm]) {
            out[atKey][uiPm] = [];
        }
        const planId = String(c.payment_plan_id);
        if (out[atKey][uiPm].some((p) => p.id === planId)) {
            continue;
        }
        out[atKey][uiPm].push({
            id: planId,
            name: c.name != null && String(c.name).trim() !== '' ? String(c.name).trim() : undefined,
            price: c.amount,
            discount: c.discount,
            installments: c.installments,
            chargeAllInstallments: c.charge_all_installments === true,
            description: c.description ?? '',
            customerType: '',
            paymentMethod: '',
        });
    }
    for (const atKey of Object.keys(out)) {
        for (const pm of Object.keys(out[atKey])) {
            const arr = out[atKey][pm];
            arr.sort((a, b) => {
                const ia = parseInt(String(a.installments), 10) || 0;
                const ib = parseInt(String(b.installments), 10) || 0;
                if (ia !== ib) {
                    return ia - ib;
                }

                return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
            });
        }
    }

    return out;
}

export function buildPlansByCustomerAndMethod(paymentPlans: PaymentPlanRow[]): Record<string, Record<string, PaymentPlanRow>> {
    const result: Record<string, Record<string, PaymentPlanRow>> = {};
    for (const plan of paymentPlans || []) {
        if (plan.deleted) continue;
        const ct = (plan.customerType || '').trim();
        const pm = (plan.paymentMethod || '').trim();
        if (!ct || !pm) continue;
        const ctKey = CUSTOMER_TYPE_MAP[ct] || ct.toLowerCase().replace(/\s/g, '_');
        const pmKey = PAYMENT_METHOD_MAP[pm] || pm.toLowerCase().replace(/\s/g, '_');
        if (!result[ctKey]) result[ctKey] = {};
        const existing = result[ctKey][pmKey];
        const planNetPrice = getPlanNetPrice(plan);
        const existingNetPrice = existing ? getPlanNetPrice(existing) : 0;
        if (!existing || (planNetPrice > 0 && planNetPrice > existingNetPrice)) {
            result[ctKey][pmKey] = plan;
        }
    }
    return result;
}

export function maskTelefone(value: string): string {
    const d = String(value).replace(/\D/g, '');
    if (d.length <= 2) return d ? `(${d}` : '';
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
}

export function maskCpf(value: string): string {
    const d = String(value).replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function isValidCpf(cpf: string): boolean {
    const digits = String(cpf || '').replace(/\D/g, '');
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits[i], 10) * (10 - i);
    let rem = sum % 11;
    const check1 = rem < 2 ? 0 : 11 - rem;
    if (check1 !== parseInt(digits[9], 10)) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits[i], 10) * (11 - i);
    rem = sum % 11;
    const check2 = rem < 2 ? 0 : 11 - rem;
    return check2 === parseInt(digits[10], 10);
}

export function maskCep(value: string): string {
    const d = String(value).replace(/\D/g, '').slice(0, 8);
    if (d.length <= 5) return d;
    return `${d.slice(0, 5)}-${d.slice(5)}`;
}

export const UFS = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' },
];
